import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';

interface VerificationCodeInputProps {
  variableName: string;
  placeholder?: string;
  className?: string;
}

const DIGIT_COUNT = 6;

const sanitizeCodeToDigits = (value: string | undefined | null): string => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  return digits.slice(0, DIGIT_COUNT);
};

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  variableName,
  placeholder
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();
  const currentSceneId = useGameStore((state) => state.currentScene?.id);

  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length: DIGIT_COUNT }, () => '')
  );
  const [isSaving, setIsSaving] = useState(false);

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const storedDigitsString = useMemo(() => sanitizeCodeToDigits(storedValue), [storedValue]);

  // Always clear local digits when the scene changes.
  // If the store hasn't yet updated and still holds the previous scene's value,
  // we temporarily skip the first store-sync to avoid briefly re-populating stale digits.
  const skipNextStoreSyncRef = useRef(false);
  const lastDigitsBeforeSceneClearRef = useRef('');
  useEffect(() => {
    lastDigitsBeforeSceneClearRef.current = storedDigitsString;
    skipNextStoreSyncRef.current = true;
    setDigits(Array.from({ length: DIGIT_COUNT }, () => ''));
  }, [currentSceneId]);

  // Sync from store when the variable value changes.
  useEffect(() => {
    if (skipNextStoreSyncRef.current) {
      // If the store is still reporting the previous-scene value, don't sync yet.
      if (storedDigitsString === lastDigitsBeforeSceneClearRef.current) {
        return;
      }
      skipNextStoreSyncRef.current = false;
    }
    const next = sanitizeCodeToDigits(storedValue).split('');
    setDigits(Array.from({ length: DIGIT_COUNT }, (_, i) => next[i] ?? ''));
  }, [storedValue, storedDigitsString]);

  const focusIndex = useCallback((idx: number) => {
    const el = inputRefs.current[idx];
    el?.focus();
    el?.select();
  }, []);

  const codeAsString = useMemo(() => digits.join(''), [digits]);
  const isComplete = useMemo(() => /^\d{6}$/.test(codeAsString), [codeAsString]);

  const commitIfComplete = useCallback(async () => {
    if (!isComplete) return;
    if (codeAsString === storedDigitsString) return;

    const ctxValue = codeAsString;
    try {
      setIsSaving(true);
      await submitUserInput(variableName, ctxValue, { maxLength: DIGIT_COUNT });
      setIsSaving(false);
    } catch (err) {
      setIsSaving(false);
      const message =
        err instanceof Error ? err.message : 'Failed to update verification code';
      uiStore.setError({
        code: 'USER_INPUT_ERROR',
        message
      });
    }
  }, [codeAsString, isComplete, storedDigitsString, submitUserInput, uiStore, variableName]);

  useEffect(() => {
    void commitIfComplete();
  }, [commitIfComplete]);

  const updateDigit = useCallback(
    (index: number, rawValue: string) => {
      const digit = rawValue.replace(/\D/g, '').slice(0, 1);
      setDigits((prev) => {
        const next = [...prev];
        next[index] = digit;
        return next;
      });
    },
    []
  );

  const handleChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      updateDigit(index, e.target.value);

      // Move forward on digit entry
      if (e.target.value.replace(/\D/g, '').length > 0) {
        const nextIdx = Math.min(DIGIT_COUNT - 1, index + 1);
        focusIndex(nextIdx);
      }
    },
    [focusIndex, updateDigit]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Backspace') return;
      const current = digits[index];

      // If current is empty, backspace jumps to previous cell.
      if (!current && index > 0) {
        e.preventDefault();
        setDigits((prev) => {
          const next = [...prev];
          next[index - 1] = '';
          return next;
        });
        focusIndex(index - 1);
      }
    },
    [digits, focusIndex]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text') ?? '';
      const digitsOnly = text.replace(/\D/g, '');
      if (digitsOnly.length !== DIGIT_COUNT) return;

      e.preventDefault();
      const arr = digitsOnly.split('');
      setDigits(Array.from({ length: DIGIT_COUNT }, (_, i) => arr[i] ?? ''));
      // After paste, focus last so the cursor feels “complete”.
      focusIndex(DIGIT_COUNT - 1);
    },
    [focusIndex]
  );

  return (
    <div
      className="verification-code-input"
      style={{
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px 0'
      }}
    >
      {Array.from({ length: DIGIT_COUNT }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            inputRefs.current[idx] = el;
          }}
          value={digits[idx]}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          placeholder={placeholder ? String(placeholder)[idx] : undefined}
          aria-label={`Verification digit ${idx + 1}`}
          style={{
            width: '44px',
            height: '52px',
            textAlign: 'center',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: '22px',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(0,0,0,0.25)',
            color: 'inherit',
            outline: 'none'
          }}
          disabled={isSaving}
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;

